/**
 * Your groups and Sets, on the phone before the day (Sam, 2026-10-22: "download everything that we need to for groups
 * and sets the day of the event or the day prior ... if they open the app for the first time the day of the event and
 * they're already out of service, we already can load it"). The map's offline set (festivalOffline) only covers events
 * with a map; this covers every group you are in and every festival you are going to, map or not, in the same window:
 * three days before the start until it is over.
 *
 * Everything lands in the same cache entries the pages read (the persisted query cache), so a page opened offline finds
 * it: a group's page, its members and their picks; a festival's lineup, your picks and the event row.
 */
import { fetchGroupPage, readGroupPage, writeGroupPage, fetchGoingCount } from './groupPageCache';
import {
  fetchEventLineup,
  fetchMyArtistVotes,
  fetchGroupMemberVotersByArtist,
} from '../api/artistVotes';
import { fetchEventDetail } from '../api/events';
import { queryKeys } from './query/queryKeys';
import { STALE, GC } from './query/staleTimes';
import { isInDownloadWindow } from './festivalOffline';

/** A group row from the Groups list (fetchUserGroups) in the shape the window reads. */
export function groupEventWindow(g) {
  return {
    event_datetime: g.eventStartsAt ?? null,
    event_date: g.eventDate ?? null,
    ends_at: g.eventEndsAt ?? null,
    multi_day: g.eventIsFestival,
  };
}

export function isGroupDue(g, now = Date.now()) {
  return Boolean(
    g?.id &&
    g.eventId &&
    !g.isPast &&
    !g.isCancelled &&
    isInDownloadWindow(groupEventWindow(g), now),
  );
}

const fetched = (queryClient, queryKey, queryFn, staleTime = STALE.user) =>
  queryClient.fetchQuery({ queryKey, queryFn, staleTime, gcTime: GC.festival });

/** A festival's Sets: the lineup (set times), your picks and the event row. Resolves true when all of it landed. */
export async function downloadSets(queryClient, eventId, userId) {
  const jobs = [
    fetched(
      queryClient,
      queryKeys.events.lineup(eventId),
      () => fetchEventLineup(eventId),
      STALE.semiStatic,
    ),
    fetched(queryClient, queryKeys.events.detail(eventId), () => fetchEventDetail(eventId)),
  ];
  if (userId)
    jobs.push(
      fetched(queryClient, queryKeys.events.myArtistVotes(eventId, userId), () =>
        fetchMyArtistVotes(eventId, userId),
      ),
    );
  const results = await Promise.allSettled(jobs);
  return results.every((r) => r.status === 'fulfilled');
}

/**
 * One group: its page (groupPageCache), and for Sets its members, your blocks and who in it picked what - under the
 * keys useEventGroupVoters reads. Then its event's Sets. Resolves true when all of it landed.
 */
export async function downloadGroup(queryClient, group, userId) {
  const groupId = group.id;
  const eventId = group.eventId;
  const res = await fetchGroupPage(groupId, userId, readGroupPage(queryClient, userId, groupId));
  if (res.status !== 'ok') return false;
  const snap = res.snap;
  try {
    snap.rsvpGoing = await fetchGoingCount(eventId);
  } catch {
    /* the count is a nice-to-have */
  }
  writeGroupPage(queryClient, userId, groupId, snap);
  if (res.membersFetched) {
    queryClient.setQueryData(['groups', 'members', groupId], snap.members);
    queryClient.setQueryData(['user-blocks', userId], [...snap.blocked]);
  }
  // The same member ids, in the same order, as useGroupMemberVoters builds its key from.
  const active = snap.members.filter((m) => !m.left_at && !snap.blocked.has(m.user_id));
  const ids = active.map((m) => m.user_id).sort();
  const jobs = [downloadSets(queryClient, eventId, userId)];
  if (ids.length) {
    const profiles = new Map(
      active.map((m) => [
        m.user_id,
        {
          id: m.user_id,
          display_name: m.profile?.display_name || 'User',
          profile_photo_url: m.profile?.profile_photo_url,
        },
      ]),
    );
    jobs.push(
      fetched(queryClient, [...queryKeys.chats.memberVoters(groupId, eventId), ...ids], () =>
        fetchGroupMemberVotersByArtist(eventId, ids, profiles),
      ),
    );
  }
  const results = await Promise.allSettled(jobs);
  return results.every((r) => r.status === 'fulfilled' && r.value !== false);
}
